import { Injectable } from '@angular/core';

import { Permission } from '../enums/permission.enum';
import { safeReadRoles, safeReadStorageString } from '../utils/auth-storage';
import { PermissionService } from './permission.service';

/**
 * Practice scope of the configuration screens (Pool, Evaluation).
 *
 * A Manager (MANAGER_READ, MANAGER_WRITE or MANAGER_PEOPLE) only works on their
 * own practice: the configuration pages show them that practice's data and hide
 * the practice picker.
 *
 * The other roles keep the unrestricted view: Director, Super Admin and CEO all
 * carry a manager permission themselves, but they are not the people this
 * restriction is for, so they are excluded by name.
 *
 * The practice is the one stored in the profile at login (userPractice), the same
 * value the rest of the app already relies on. It is read straight from storage
 * instead of through AuthService, which keeps this service free of the HTTP and
 * router dependencies and therefore easy to use in isolation.
 */
@Injectable({ providedIn: 'root' })
export class PracticeScopeService {
  /**
   * Permissions that put a user under the practice restriction. MANAGER_WRITE is
   * included alongside MANAGER_READ and MANAGER_PEOPLE: a manager who can write
   * must be scoped exactly like one who can only read.
   */
  private static readonly MANAGER_PERMISSIONS = [
    Permission.MANAGER_READ,
    Permission.MANAGER_WRITE,
    Permission.MANAGER_PEOPLE,
  ];

  /** Roles that keep the full view, even though they carry a manager permission. */
  private static readonly UNRESTRICTED_ROLES = ['director', 'superadmin', 'ceo'];

  constructor(private readonly permissionService: PermissionService) {}

  /** True when the user is a Manager and must stay in their own practice. */
  isPracticeScopedUser(): boolean {
    if (!this.permissionService.hasAnyPermission(PracticeScopeService.MANAGER_PERMISSIONS)) {
      return false;
    }
    return !this.hasAnyRole(PracticeScopeService.UNRESTRICTED_ROLES);
  }

  /**
   * The practice the user is confined to, or null when they are unrestricted.
   *
   * A scoped user with no practice on their profile gets an empty string, which
   * matches no practice: they see nothing rather than everything.
   */
  getScopedPractice(): string | null {
    if (!this.isPracticeScopedUser()) {
      return null;
    }
    return safeReadStorageString('userPractice')?.trim() || '';
  }

  /** Keeps only the entries of the user's practice. Unrestricted users get the list back untouched. */
  restrict<T>(items: T[], practiceOf: (item: T) => string | null | undefined): T[] {
    const scoped = this.getScopedPractice();
    if (scoped === null) {
      return items;
    }
    const target = scoped.toLowerCase();
    return items.filter((item) => (practiceOf(item) ?? '').trim().toLowerCase() === target);
  }

  /** True when the given practice is the one the user may work on. */
  isAllowedPractice(practice: string | null | undefined): boolean {
    const scoped = this.getScopedPractice();
    if (scoped === null) {
      return true;
    }
    return (practice ?? '').trim().toLowerCase() === scoped.toLowerCase();
  }

  /** True when the user holds one of the given roles. */
  private hasAnyRole(names: readonly string[]): boolean {
    return safeReadRoles().some((role) => names.includes(PracticeScopeService.normalizeRole(role)));
  }

  /** ROLE_MANAGER, "Manager" and "manager people" all collapse to a comparable name. */
  private static normalizeRole(role: string): string {
    return role.replace(/^ROLE_/i, '').trim().toLowerCase().replace(/[_\s]+/g, '');
  }
}
